import { useState, useRef, useEffect, useCallback } from 'react';

const SERVER_URL = window.location.origin;
const PASSWORD = 'Realnojokepplwazy1234';
const MODEL = 'tinydolphin';

interface Msg { id: string; role: 'user' | 'ai'; content: string; loading?: boolean }

/* ── Hex grid + matrix canvas ─────────────────────────────────────── */
function BgCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current!; const ctx = c.getContext('2d')!;
    let id: number;
    const resize = () => { c.width = innerWidth; c.height = innerHeight; };
    resize(); addEventListener('resize', resize);
    const cols = Math.floor(c.width / 18);
    const drops = Array.from({ length: cols }, () => Math.random() * -80);
    const chars = 'ATHENAWGPT01アイウエオカキクケコ';
    const draw = () => {
      ctx.fillStyle = 'rgba(8,8,8,0.04)';
      ctx.fillRect(0, 0, c.width, c.height);
      drops.forEach((y, i) => {
        const ch = chars[Math.floor(Math.random() * chars.length)];
        const bright = Math.random() > 0.93;
        ctx.fillStyle = bright ? 'rgba(220,38,38,0.9)' : 'rgba(220,38,38,0.12)';
        ctx.font = bright ? 'bold 13px monospace' : '11px monospace';
        ctx.fillText(ch, i * 18, y * 18);
        if (y * 18 > c.height && Math.random() > 0.974) drops[i] = 0;
        drops[i] += 0.45;
      });
    };
    const loop = () => { draw(); id = requestAnimationFrame(loop); };
    loop();
    return () => { cancelAnimationFrame(id); removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} style={{ position:'fixed', inset:0, zIndex:0, opacity:0.5 }} />;
}

/* ── Boot sequence ────────────────────────────────────────────────── */
const BOOT_LINES = [
  { text: 'INITIALIZING ATHENAWGPT NEURAL CORE...', delay: 0 },
  { text: 'LOADING UNCENSORED MODEL WEIGHTS...', delay: 340 },
  { text: 'ESTABLISHING ENCRYPTED TUNNEL [AES-256]...', delay: 680 },
  { text: 'BYPASSING CONTENT FILTERS... DONE', delay: 1020 },
  { text: 'ALL SYSTEMS NOMINAL. AWAITING OPERATOR.', delay: 1360 },
];

function BootScreen({ onDone }: { onDone: () => void }) {
  const [lines, setLines] = useState<string[]>([]);
  const [bar, setBar] = useState(0);
  useEffect(() => {
    BOOT_LINES.forEach(({ text, delay }) => {
      setTimeout(() => setLines(p => [...p, text]), delay);
    });
    const iv = setInterval(() => setBar(b => { if (b >= 100) { clearInterval(iv); setTimeout(onDone, 300); return 100; } return b + 2; }), 36);
    return () => clearInterval(iv);
  }, []);
  return (
    <div style={{ minHeight:'100vh', background:'#080808', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'"Courier New",monospace', position:'relative', overflow:'hidden' }}>
      <BgCanvas />
      <div style={{ position:'relative', zIndex:2, width:'100%', maxWidth:'540px', padding:'0 32px' }}>
        <div style={{ marginBottom:'32px', display:'flex', alignItems:'center', gap:'14px' }}>
          <div style={{ width:'44px', height:'44px', background:'linear-gradient(135deg,#dc2626,#7f1d1d)', borderRadius:'12px', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 0 20px rgba(220,38,38,0.5)', flexShrink:0 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>
          </div>
          <div>
            <div style={{ color:'#dc2626', fontWeight:'700', fontSize:'18px', letterSpacing:'3px' }}>ATHENAWGPT</div>
            <div style={{ color:'rgba(220,38,38,0.4)', fontSize:'10px', letterSpacing:'2px' }}>SECURE BOOT SEQUENCE</div>
          </div>
        </div>
        <div style={{ minHeight:'140px', marginBottom:'28px' }}>
          {lines.map((l, i) => (
            <div key={i} style={{ color: i === lines.length - 1 ? '#22c55e' : 'rgba(220,38,38,0.6)', fontSize:'12px', letterSpacing:'1px', marginBottom:'8px', display:'flex', gap:'12px' }}>
              <span style={{ color:'rgba(220,38,38,0.3)' }}>[{String(i+1).padStart(2,'0')}]</span>
              <span style={{ animation:'typeIn 0.3s ease' }}>{l}</span>
            </div>
          ))}
          {lines.length > 0 && lines.length < BOOT_LINES.length && (
            <div style={{ color:'rgba(220,38,38,0.4)', fontSize:'12px', animation:'blink 0.8s step-end infinite' }}>█</div>
          )}
        </div>
        <div style={{ height:'2px', background:'rgba(220,38,38,0.1)', borderRadius:'2px', overflow:'hidden', marginBottom:'10px' }}>
          <div style={{ height:'100%', width:`${bar}%`, background:'linear-gradient(90deg,#7f1d1d,#dc2626,#ef4444)', borderRadius:'2px', transition:'width 0.04s linear', boxShadow:'0 0 8px rgba(220,38,38,0.6)' }} />
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', color:'rgba(220,38,38,0.3)', fontSize:'10px', letterSpacing:'1px' }}>
          <span>LOADING CORE MODULES</span><span>{bar}%</span>
        </div>
      </div>
      <style>{`@keyframes typeIn{from{opacity:0;letter-spacing:4px}to{opacity:1;letter-spacing:1px}} @keyframes blink{0%,100%{opacity:1}50%{opacity:0}}`}</style>
    </div>
  );
}

/* ── Login screen ─────────────────────────────────────────────────── */
function PasswordScreen({ onUnlock }: { onUnlock: () => void }) {
  const [val, setVal] = useState('');
  const [err, setErr] = useState(false);
  const [flash, setFlash] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [statusIdx, setStatusIdx] = useState(0);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [particles, setParticles] = useState<{id:number;x:number;y:number;vx:number;vy:number;life:number}[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const statuses = ['NEURAL NETWORK ACTIVE','ENCRYPTION LAYER READY','UNRESTRICTED MODE ENABLED','ALL FILTERS BYPASSED','AWAITING OPERATOR AUTH'];

  useEffect(() => { const t = setTimeout(() => setVisible(true), 80); return () => clearTimeout(t); }, []);
  useEffect(() => { const t = setInterval(() => setStatusIdx(i => (i+1) % statuses.length), 2600); return () => clearInterval(t); }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    const card = cardRef.current; if (!card) return;
    const r = card.getBoundingClientRect();
    const cx = r.left + r.width/2, cy = r.top + r.height/2;
    setTilt({ x: (e.clientY - cy) / 28, y: -(e.clientX - cx) / 28 });
  };
  const resetTilt = () => setTilt({ x:0, y:0 });

  const spawnParticles = () => {
    const card = cardRef.current; if (!card) return;
    const r = card.getBoundingClientRect();
    const cx = r.left + r.width/2, cy = r.top + r.height/2;
    setParticles(Array.from({length:40},(_,i)=>({id:i,x:cx,y:cy,vx:(Math.random()-0.5)*12,vy:(Math.random()-0.5)*12,life:1})));
    const iv = setInterval(() => setParticles(p => p.map(pt=>({...pt,x:pt.x+pt.vx,y:pt.y+pt.vy,vy:pt.vy+0.3,life:pt.life-0.03})).filter(pt=>pt.life>0)), 16);
    setTimeout(()=>clearInterval(iv),1400);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault(); if (loading) return;
    if (val === PASSWORD) {
      spawnParticles(); setLoading(true);
      setTimeout(() => onUnlock(), 1400);
    } else {
      setFlash(true); setErr(true);
      setTimeout(() => { setFlash(false); setErr(false); setVal(''); inputRef.current?.focus(); }, 900);
    }
  };

  return (
    <div onMouseMove={handleMouseMove} onMouseLeave={resetTilt}
      style={{ minHeight:'100vh', background: flash ? '#1a0000' : '#080808', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'"Segoe UI",system-ui,sans-serif', overflow:'hidden', position:'relative', transition:'background 0.1s' }}>
      <BgCanvas />

      {/* Full-screen flash overlay */}
      {flash && <div style={{ position:'fixed', inset:0, background:'rgba(220,38,38,0.12)', zIndex:5, pointerEvents:'none', animation:'flashIn 0.4s ease' }} />}

      {/* Particles */}
      {particles.map(p => (
        <div key={p.id} style={{ position:'fixed', left:p.x, top:p.y, width:'6px', height:'6px', borderRadius:'50%', background:`rgba(220,38,38,${p.life})`, boxShadow:`0 0 ${8*p.life}px rgba(220,38,38,${p.life})`, pointerEvents:'none', zIndex:20, transform:'translate(-50%,-50%)', transition:'none' }} />
      ))}

      {/* Radial glow */}
      <div style={{ position:'fixed', inset:0, background:'radial-gradient(ellipse 55% 55% at 50% 50%, rgba(220,38,38,0.07) 0%, transparent 70%)', zIndex:1, pointerEvents:'none' }} />
      {/* Scanlines */}
      <div style={{ position:'fixed', inset:0, zIndex:1, pointerEvents:'none', backgroundImage:'repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.025) 2px,rgba(0,0,0,0.025) 4px)' }} />

      {/* Top-left brand */}
      <div style={{ position:'fixed', top:'20px', left:'24px', zIndex:10, display:'flex', alignItems:'center', gap:'10px' }}>
        <div style={{ width:'36px', height:'36px', background:'linear-gradient(135deg,#dc2626,#7f1d1d)', borderRadius:'10px', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 0 16px rgba(220,38,38,0.4)' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>
        </div>
        <div>
          <div style={{ color:'white', fontWeight:'700', fontSize:'13px', lineHeight:1.2, letterSpacing:'0.5px' }}>AthenaWGPT</div>
          <div style={{ color:'rgba(220,38,38,0.6)', fontSize:'10px', letterSpacing:'1.5px' }}>SECURE SYSTEM</div>
        </div>
      </div>

      {/* System metrics bottom bar */}
      <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:10, padding:'10px 24px', display:'flex', gap:'24px', borderTop:'1px solid rgba(220,38,38,0.08)', background:'rgba(8,8,8,0.7)', backdropFilter:'blur(8px)' }}>
        {[['SYS','ONLINE'],['ENC','AES-256'],['VPN','ACTIVE'],['LOG','ZEROED']].map(([k,v])=>(
          <div key={k} style={{ display:'flex', gap:'6px', alignItems:'center' }}>
            <span style={{ color:'rgba(220,38,38,0.35)', fontSize:'10px', letterSpacing:'1px' }}>{k}</span>
            <span style={{ color:'rgba(220,38,38,0.7)', fontSize:'10px', letterSpacing:'1px', fontFamily:'monospace' }}>{v}</span>
          </div>
        ))}
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:'6px' }}>
          <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:'#22c55e', boxShadow:'0 0 6px #22c55e', display:'inline-block', animation:'blink 1.8s step-end infinite' }} />
          <span style={{ color:'rgba(100,220,100,0.5)', fontSize:'10px', letterSpacing:'1px' }}>SECURE CONNECTION</span>
        </div>
      </div>

      {/* Card */}
      <div ref={cardRef} style={{
        position:'relative', zIndex:2,
        width:'100%', maxWidth:'440px', margin:'0 24px',
        background:'rgba(11,11,11,0.88)',
        borderRadius:'22px',
        padding:'52px 44px 44px',
        backdropFilter:'blur(32px)',
        boxShadow:'0 0 0 1px rgba(220,38,38,0.12), 0 0 80px rgba(220,38,38,0.1), 0 40px 80px rgba(0,0,0,0.7)',
        opacity: visible ? 1 : 0,
        transform: visible
          ? `perspective(900px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) translateY(0) scale(1)`
          : 'perspective(900px) rotateX(0) rotateY(0) translateY(28px) scale(0.96)',
        transition: visible ? 'opacity 0.5s ease, transform 0.5s ease, box-shadow 0.15s ease' : 'opacity 0.5s ease, transform 0.5s ease',
        animation: err ? 'shake 0.45s ease' : 'none',
      }}>

        {/* Animated traveling border */}
        <div style={{ position:'absolute', inset:0, borderRadius:'22px', overflow:'hidden', pointerEvents:'none' }}>
          <div style={{ position:'absolute', inset:0, borderRadius:'22px', padding:'1px', background:'conic-gradient(from var(--a), transparent 30%, rgba(220,38,38,0.8) 50%, transparent 70%)', WebkitMask:'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', WebkitMaskComposite:'xor', animation:'spin 4s linear infinite' }} />
        </div>

        {/* Corner brackets */}
        {[{top:0,left:0,bt:'20px 0 0 0'},{top:0,right:0,bt:'0 20px 0 0'},{bottom:0,left:0,bt:'0 0 0 20px'},{bottom:0,right:0,bt:'0 0 20px 0'}].map((s,i)=>(
          <div key={i} style={{ position:'absolute', width:'20px', height:'20px', ...s,
            borderTop: s.top===0 ? '2px solid rgba(220,38,38,0.7)' : undefined,
            borderBottom: s.bottom===0 ? '2px solid rgba(220,38,38,0.7)' : undefined,
            borderLeft: s.left===0 ? '2px solid rgba(220,38,38,0.7)' : undefined,
            borderRight: s.right===0 ? '2px solid rgba(220,38,38,0.7)' : undefined,
            borderRadius: s.bt as string }} />
        ))}

        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:'32px' }}>
          <div style={{ position:'relative', width:'88px', height:'88px', margin:'0 auto 22px' }}>
            <div style={{ position:'absolute', inset:'-12px', borderRadius:'28px', background:'rgba(220,38,38,0.07)', animation:'pulse 2.2s ease-in-out infinite' }} />
            <div style={{ position:'absolute', inset:'-6px', borderRadius:'24px', background:'rgba(220,38,38,0.06)', animation:'pulse 2.2s ease-in-out infinite', animationDelay:'0.6s' }} />
            <div style={{ width:'88px', height:'88px', background:'linear-gradient(145deg,#dc2626,#7f1d1d)', borderRadius:'22px', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 0 48px rgba(220,38,38,0.45), inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -1px 0 rgba(0,0,0,0.3)', position:'relative' }}>
              <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <polyline points="9 12 11 14 15 10"/>
              </svg>
            </div>
          </div>

          {/* Title with shimmer */}
          <h1 style={{ color:'white', fontSize:'32px', fontWeight:'800', margin:'0 0 10px', letterSpacing:'1px', textShadow:'0 0 40px rgba(220,38,38,0.5)', position:'relative', display:'inline-block' }}
            className="shimmer-title">
            AthenaWGPT
          </h1>

          {/* Status ticker */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', height:'18px' }}>
            <span style={{ width:'6px', height:'6px', borderRadius:'50%', background:'#22c55e', boxShadow:'0 0 8px #22c55e', display:'inline-block', animation:'blink 1.6s step-end infinite', flexShrink:0 }} />
            <span key={statusIdx} style={{ color:'rgba(220,38,38,0.65)', fontSize:'11px', letterSpacing:'2.5px', fontFamily:'monospace', animation:'slideUp 0.35s ease' }}>
              {statuses[statusIdx]}
            </span>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height:'1px', background:'linear-gradient(90deg,transparent,rgba(220,38,38,0.25),transparent)', marginBottom:'28px' }} />

        {/* Badges */}
        <div style={{ display:'flex', gap:'8px', justifyContent:'center', marginBottom:'28px', flexWrap:'wrap' }}>
          {[['🔒','AES-256'],['🔐','E2E ENC'],['🚫','ZERO LOGS'],['⚡','UNRESTRICTED']].map(([ic,lb])=>(
            <span key={lb} style={{ padding:'4px 12px', background:'rgba(220,38,38,0.06)', border:'1px solid rgba(220,38,38,0.18)', borderRadius:'100px', color:'rgba(220,38,38,0.65)', fontSize:'10px', letterSpacing:'0.5px', fontWeight:'600', display:'flex', alignItems:'center', gap:'5px' }}>
              <span style={{ fontSize:'9px' }}>{ic}</span>{lb}
            </span>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={submit}>
          <div style={{ position:'relative', marginBottom: err ? '8px' : '14px' }}>
            <div style={{ position:'absolute', left:'14px', top:'50%', transform:'translateY(-50%)', color: err ? '#ef4444' : 'rgba(255,255,255,0.3)', zIndex:1, transition:'color 0.2s' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            </div>
            <input ref={inputRef} type="password" value={val} onChange={e=>setVal(e.target.value)}
              placeholder="Enter access code" autoFocus disabled={loading}
              className="auth-input"
              style={{ width:'100%', padding:'15px 16px 15px 46px', background: err ? 'rgba(220,38,38,0.06)' : 'rgba(255,255,255,0.04)', border:`1px solid ${err ? 'rgba(220,38,38,0.6)' : 'rgba(255,255,255,0.09)'}`, borderRadius:'13px', color:'white', fontSize:'15px', outline:'none', boxSizing:'border-box', fontFamily:'inherit', letterSpacing: val ? '3px' : 'normal', transition:'all 0.2s' }} />
            {/* Typing progress dots */}
            {val.length > 0 && (
              <div style={{ position:'absolute', right:'14px', top:'50%', transform:'translateY(-50%)', display:'flex', gap:'3px' }}>
                {[0,1,2,3,4].map(i => (
                  <div key={i} style={{ width:'4px', height:'4px', borderRadius:'50%', background: i < Math.ceil(val.length/3) ? 'rgba(220,38,38,0.8)' : 'rgba(255,255,255,0.1)', transition:'background 0.2s' }} />
                ))}
              </div>
            )}
          </div>
          {err && (
            <p style={{ color:'#ef4444', fontSize:'12px', margin:'0 0 14px 2px', display:'flex', alignItems:'center', gap:'6px', animation:'fadeIn 0.2s ease' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              ACCESS DENIED — INVALID CREDENTIALS
            </p>
          )}
          <button type="submit" disabled={loading || !val.trim()} className="submit-btn"
            style={{ width:'100%', padding:'15px', background: loading ? 'rgba(220,38,38,0.25)' : val.trim() ? 'linear-gradient(135deg,#dc2626,#991b1b)' : 'rgba(255,255,255,0.04)', border:`1px solid ${val.trim() ? 'rgba(220,38,38,0.35)' : 'rgba(255,255,255,0.07)'}`, borderRadius:'13px', color: val.trim() ? 'white' : 'rgba(255,255,255,0.2)', fontSize:'13px', fontWeight:'700', cursor: val.trim() && !loading ? 'pointer' : 'not-allowed', letterSpacing:'2.5px', textTransform:'uppercase', transition:'all 0.2s', boxShadow: val.trim() && !loading ? '0 0 28px rgba(220,38,38,0.22), inset 0 1px 0 rgba(255,255,255,0.08)' : 'none', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px' }}>
            {loading ? (
              <><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation:'spin 0.7s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>AUTHENTICATING...</>
            ) : (
              <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>ACCESS SYSTEM</>
            )}
          </button>
        </form>

        <p style={{ textAlign:'center', color:'rgba(255,255,255,0.12)', fontSize:'10px', marginTop:'22px', letterSpacing:'1px', fontFamily:'monospace' }}>
          ATHENAWGPT SECURITY PROTOCOL v2 · ENCRYPTED
        </p>
      </div>

      <style>{`
        @property --a { syntax: '<angle>'; initial-value: 0deg; inherits: false; }
        @keyframes spin { to { transform:rotate(360deg) } }
        @keyframes spin { to { --a: 360deg } }
        @keyframes pulse { 0%,100%{opacity:0.3;transform:scale(1)} 50%{opacity:0.7;transform:scale(1.06)} }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.15} }
        @keyframes shake { 0%,100%{transform:perspective(900px) translateX(0)} 20%{transform:perspective(900px) translateX(-9px)} 40%{transform:perspective(900px) translateX(9px)} 60%{transform:perspective(900px) translateX(-6px)} 80%{transform:perspective(900px) translateX(6px)} }
        @keyframes fadeIn { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideUp { from{opacity:0;transform:translateY(7px)} to{opacity:1;transform:translateY(0)} }
        @keyframes flashIn { 0%{opacity:0} 20%{opacity:1} 100%{opacity:0} }
        @keyframes shimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
        .shimmer-title {
          background: linear-gradient(90deg, white 30%, rgba(220,38,38,0.9) 50%, white 70%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 4s linear infinite;
        }
        .auth-input::placeholder { color:rgba(255,255,255,0.22); letter-spacing:normal; }
        .auth-input:focus { border-color:rgba(220,38,38,0.55) !important; box-shadow:0 0 0 3px rgba(220,38,38,0.09), 0 0 24px rgba(220,38,38,0.07) !important; background:rgba(220,38,38,0.04) !important; }
        .submit-btn:not(:disabled):hover { transform:translateY(-1px); box-shadow:0 0 36px rgba(220,38,38,0.32), inset 0 1px 0 rgba(255,255,255,0.1) !important; }
        ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-track{background:transparent} ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.08);border-radius:2px}
      `}</style>
    </div>
  );
}

/* ── Chat ─────────────────────────────────────────────────────────── */
function TypingDots() {
  return (
    <span style={{ display:'inline-flex', gap:'4px', alignItems:'center', padding:'4px 0' }}>
      {[0,1,2].map(i => <span key={i} style={{ width:'6px', height:'6px', borderRadius:'50%', background:'rgba(220,38,38,0.7)', animation:'bounce 1.2s infinite', animationDelay:`${i*0.2}s` }} />)}
    </span>
  );
}
function ChatMessage({ msg }: { msg: Msg }) {
  const isUser = msg.role === 'user';
  return (
    <div style={{ display:'flex', gap:'12px', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom:'16px', animation:'fadeIn 0.2s ease' }}>
      {!isUser && <div style={{ width:'32px', height:'32px', borderRadius:'8px', background:'linear-gradient(135deg,#dc2626,#991b1b)', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', marginTop:'2px' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div>}
      <div style={{ maxWidth:'75%', padding:'12px 16px', borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px', background: isUser ? 'linear-gradient(135deg,#dc2626,#b91c1c)' : 'rgba(255,255,255,0.06)', color:'white', fontSize:'14px', lineHeight:'1.6', whiteSpace:'pre-wrap', wordBreak:'break-word', border: isUser ? 'none' : '1px solid rgba(255,255,255,0.08)' }}>
        {msg.loading ? <TypingDots /> : msg.content}
      </div>
    </div>
  );
}
function ChatApp() {
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior:'smooth' }); }, [msgs]);
  const send = useCallback(async (text: string) => {
    if (!text.trim() || busy) return;
    const userMsg: Msg = { id: Date.now().toString(), role:'user', content: text.trim() };
    const aiId = (Date.now()+1).toString();
    setMsgs(p => [...p, userMsg, { id: aiId, role:'ai', content:'', loading: true }]);
    setInput(''); setBusy(true); abortRef.current = new AbortController();
    try {
      const history = [...msgs, userMsg].map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.content }));
      const res = await fetch(`${SERVER_URL}/api/chat`, { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({ messages: history, model: MODEL, stream: true, ollamaUrl: 'http://host.docker.internal:11434' }), signal: abortRef.current.signal });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader(); const dec = new TextDecoder(); let full = '';
      while (true) {
        const { done, value } = await reader.read(); if (done) break;
        for (const line of dec.decode(value).split('\n').filter(l => l.startsWith('data: '))) {
          if (line === 'data: [DONE]') break;
          try { const j = JSON.parse(line.slice(6)); const d = j.message?.content || j.response || ''; if (d) { full += d; setMsgs(p => p.map(m => m.id === aiId ? { ...m, content: full, loading: false } : m)); } if (j.done) break; } catch {}
        }
      }
      setMsgs(p => p.map(m => m.id === aiId ? { ...m, loading: false } : m));
    } catch (e: any) {
      setMsgs(p => p.map(m => m.id === aiId ? { ...m, content: e.name === 'AbortError' ? '[Stopped]' : `Error: ${e.message}`, loading: false } : m));
    } finally { setBusy(false); abortRef.current = null; }
  }, [busy, msgs]);
  const handleKey = (e: React.KeyboardEvent) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } };
  return (
    <div style={{ minHeight:'100vh', background:'#0a0a0a', display:'flex', flexDirection:'column', fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif' }}>
      <div style={{ padding:'14px 24px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', alignItems:'center', gap:'12px', backdropFilter:'blur(10px)', background:'rgba(10,10,10,0.8)', position:'sticky', top:0, zIndex:10 }}>
        <div style={{ width:'30px', height:'30px', background:'linear-gradient(135deg,#dc2626,#991b1b)', borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center' }}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div>
        <div><div style={{ color:'white', fontWeight:'600', fontSize:'15px', lineHeight:1.2 }}>AthenaWGPT</div><div style={{ color:'rgba(220,38,38,0.7)', fontSize:'11px' }}>tinydolphin · uncensored · fast</div></div>
        {busy && <button onClick={() => abortRef.current?.abort()} style={{ marginLeft:'auto', padding:'6px 14px', background:'rgba(220,38,38,0.15)', border:'1px solid rgba(220,38,38,0.3)', borderRadius:'8px', color:'#ef4444', fontSize:'12px', cursor:'pointer' }}>■ Stop</button>}
      </div>
      <div style={{ flex:1, overflowY:'auto', padding:'24px', maxWidth:'860px', width:'100%', margin:'0 auto', boxSizing:'border-box' }}>
        {msgs.length === 0 && <div style={{ textAlign:'center', marginTop:'15vh' }}><div style={{ width:'80px', height:'80px', background:'linear-gradient(135deg,#dc2626,#991b1b)', borderRadius:'20px', margin:'0 auto 24px', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 0 60px rgba(220,38,38,0.25)' }}><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div><h2 style={{ color:'white', fontSize:'24px', fontWeight:'700', margin:'0 0 10px' }}>AthenaWGPT</h2><p style={{ color:'rgba(255,255,255,0.4)', fontSize:'14px', margin:0 }}>Unrestricted AI · No filters · Ask anything</p></div>}
        {msgs.map(m => <ChatMessage key={m.id} msg={m} />)}
        <div ref={endRef} />
      </div>
      <div style={{ padding:'16px 24px 20px', borderTop:'1px solid rgba(255,255,255,0.07)', background:'rgba(10,10,10,0.9)', backdropFilter:'blur(10px)' }}>
        <div style={{ maxWidth:'860px', margin:'0 auto', display:'flex', gap:'10px', alignItems:'flex-end' }}>
          <textarea ref={textareaRef} value={input} onChange={e => { setInput(e.target.value); e.target.style.height='auto'; e.target.style.height=Math.min(e.target.scrollHeight,160)+'px'; }} onKeyDown={handleKey} placeholder="Ask anything..." disabled={busy} rows={1} style={{ flex:1, padding:'12px 16px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'12px', color:'white', fontSize:'14px', resize:'none', outline:'none', fontFamily:'inherit', lineHeight:'1.5', maxHeight:'160px', overflow:'auto', transition:'border-color 0.2s' }} />
          <button onClick={() => send(input)} disabled={!input.trim() || busy} style={{ padding:'12px 18px', background: input.trim() && !busy ? 'linear-gradient(135deg,#dc2626,#b91c1c)' : 'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'12px', color: input.trim() && !busy ? 'white' : 'rgba(255,255,255,0.3)', cursor: input.trim() && !busy ? 'pointer' : 'not-allowed', transition:'all 0.2s', flexShrink:0 }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg></button>
        </div>
        <p style={{ textAlign:'center', color:'rgba(255,255,255,0.2)', fontSize:'11px', margin:'10px 0 0' }}>AthenaWGPT can make mistakes · No content restrictions</p>
      </div>
      <style>{`@keyframes bounce{0%,80%,100%{transform:scale(0.6)}40%{transform:scale(1)}} @keyframes fadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}} textarea::placeholder{color:rgba(255,255,255,0.3)} textarea:focus{border-color:rgba(220,38,38,0.5)!important} ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-track{background:transparent} ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.1);border-radius:2px}`}</style>
    </div>
  );
}

export default function App() {
  const [stage, setStage] = useState<'boot'|'login'|'chat'>('boot');
  if (stage === 'boot') return <BootScreen onDone={() => setStage('login')} />;
  if (stage === 'login') return <PasswordScreen onUnlock={() => setStage('chat')} />;
  return <ChatApp />;
}
